import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import { ArrowLeft, Loader2, File, FileText } from "lucide-react";
import ProjectTabs from "../components/ProjectTabs";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ProjectFilesPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState("");
  const [fileLoading, setFileLoading] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API_URL}/api/projects/${projectId}/files`, auth)
      .then(({ data }) => {
        setTree(data.tree || []);
        const claude = (data.tree || []).find((f) => f.toUpperCase().endsWith("CLAUDE.MD"));
        const readme = (data.tree || []).find((f) => f.toUpperCase().endsWith("README.MD"));
        const first = claude || readme;
        if (first) openFile(first);
      })
      .catch(() => toast.error("Impossible de lister les fichiers"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [projectId]);

  const openFile = async (path) => {
    setSelected(path);
    setFileLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/projects/${projectId}/file`, { ...auth, params: { path } });
      setContent(data.content);
    } catch (e) {
      setContent("");
      toast.error("Fichier illisible ou trop volumineux");
    } finally {
      setFileLoading(false);
    }
  };

  const Tabs = () => (
    <ProjectTabs projectId={projectId} actif="fichiers" />
  );

  if (loading) {
    return <CRMLayout><div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div></CRMLayout>;
  }

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8 max-w-6xl flex flex-col" style={{ minHeight: "calc(100vh - 4rem)" }}>
        <Link to="/dashboard/projects" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour aux projets
        </Link>
        <Tabs />

        {tree.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
            Aucun fichier — renseignez le dossier du projet (onglet Aperçu) pour explorer le code.
          </div>
        ) : (
          <div className="flex gap-4 flex-1 min-h-0">
            {/* Arborescence */}
            <div className="w-64 shrink-0 bg-white border border-slate-200 rounded-xl shadow-sm overflow-y-auto p-2">
              {tree.map((f) => (
                <button key={f} onClick={() => openFile(f)}
                  className={`w-full flex items-center gap-1.5 text-left rounded px-2 py-1.5 text-xs hover:bg-slate-100 ${selected === f ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-600"}`}>
                  {f.toUpperCase().endsWith(".MD") ? <FileText className="w-3.5 h-3.5 shrink-0" /> : <File className="w-3.5 h-3.5 shrink-0 text-slate-300" />}
                  <span className="truncate">{f}</span>
                </button>
              ))}
            </div>

            {/* Contenu */}
            <div className="flex-1 min-w-0 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
              <div className="px-4 py-2.5 border-b border-slate-200 text-sm font-mono text-slate-600 truncate">{selected || "Sélectionnez un fichier"}</div>
              <div className="flex-1 overflow-auto">
                {fileLoading ? (
                  <div className="flex justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : (
                  <pre className="text-xs font-mono leading-relaxed p-4 whitespace-pre-wrap">{content}</pre>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </CRMLayout>
  );
}
